/**
 * Validation utilities for lot data
 */

// Validation rules
export const VALIDATION_RULES = {
  lotNumber: {
    pattern: /^[A-Za-z0-9][A-Za-z0-9]*$/, // Can start with letter or number, then letters/numbers
    message: 'Lot number must contain only letters and numbers',
    maxLength: 20
  },
  lotSize: {
    pattern: /^(\d+(?:\.\d+)?)\s*(m²|sqm|sq\.m|square\s*meters?|hectares?|acres?|sq\s*ft|square\s*feet?)$/i,
    message: 'Lot size must be a number followed by a unit (e.g., "500 m²", "0.5 hectares", "1200 sq ft")',
    maxLength: 50
  },
  frontage: {
    pattern: /^(\d+(?:\.\d+)?)\s*(m|meters?|ft|feet?)$/i,
    message: 'Frontage must be a number followed by a unit (e.g., "20m", "65 ft")',
    maxLength: 20
  },
  price: {
    min: 0,
    max: 999999999.99,
    message: 'Price must be between 0 and 999,999,999.99'
  },
  notes: {
    maxLength: 1000,
    message: 'Notes cannot exceed 1000 characters'
  },
  stage: {
    pattern: /^[A-Za-z0-9\s\-_]+$/,
    message: 'Stage name can only contain letters, numbers, spaces, hyphens, and underscores',
    maxLength: 50
  }
}

// Unit conversion helpers
export const UNIT_CONVERSIONS = {
  // Area units to square meters
  'm²': 1,
  'sqm': 1,
  'sq.m': 1,
  'square meters': 1,
  'square meter': 1,
  'hectares': 10000,
  'hectare': 10000,
  'acres': 4046.86,
  'acre': 4046.86,
  'sq ft': 0.092903,
  'square feet': 0.092903,
  'square foot': 0.092903
}

// Length units to meters
export const LENGTH_UNITS = {
  'm': 1,
  'meters': 1,
  'meter': 1,
  'ft': 0.3048,
  'feet': 0.3048,
  'foot': 0.3048
}

/**
 * Validates lot number
 * @param {string} lotNumber - The lot number to validate
 * @returns {object} - { isValid: boolean, message: string }
 */
export function validateLotNumber(lotNumber) {
  if (!lotNumber || lotNumber.trim() === '') {
    return { isValid: true, message: '' } // Optional field
  }

  const trimmed = lotNumber.trim()
  
  if (trimmed.length > VALIDATION_RULES.lotNumber.maxLength) {
    return { 
      isValid: false, 
      message: `Lot number cannot exceed ${VALIDATION_RULES.lotNumber.maxLength} characters` 
    }
  }

  if (!VALIDATION_RULES.lotNumber.pattern.test(trimmed)) {
    return { 
      isValid: false, 
      message: VALIDATION_RULES.lotNumber.message 
    }
  }

  return { isValid: true, message: '' }
}

/**
 * Validates lot size
 * @param {string} lotSize - The lot size to validate
 * @returns {object} - { isValid: boolean, message: string, normalizedValue?: string }
 */
export function validateLotSize(lotSize) {
  if (!lotSize || lotSize.trim() === '') {
    return { isValid: true, message: '' } // Optional field
  }

  const trimmed = lotSize.trim()
  
  if (trimmed.length > VALIDATION_RULES.lotSize.maxLength) {
    return { 
      isValid: false, 
      message: `Lot size cannot exceed ${VALIDATION_RULES.lotSize.maxLength} characters` 
    }
  }

  const match = trimmed.match(VALIDATION_RULES.lotSize.pattern)
  if (!match) {
    return { 
      isValid: false, 
      message: VALIDATION_RULES.lotSize.message 
    }
  }

  // Extract number and unit
  const number = parseFloat(match[1])
  const unit = match[2].toLowerCase()

  if (isNaN(number) || number <= 0) {
    return { 
      isValid: false, 
      message: 'Lot size must be a positive number' 
    }
  }

  // Normalize the value for storage
  const normalizedValue = `${number} ${unit}`
  
  return { 
    isValid: true, 
    message: '', 
    normalizedValue 
  }
}

/**
 * Validates frontage
 * @param {string} frontage - The frontage to validate
 * @returns {object} - { isValid: boolean, message: string, normalizedValue?: string }
 */
export function validateFrontage(frontage) {
  if (!frontage || frontage.trim() === '') {
    return { isValid: true, message: '' } // Optional field
  }

  const trimmed = frontage.trim()
  
  if (trimmed.length > VALIDATION_RULES.frontage.maxLength) {
    return { 
      isValid: false, 
      message: `Frontage cannot exceed ${VALIDATION_RULES.frontage.maxLength} characters` 
    }
  }

  const match = trimmed.match(VALIDATION_RULES.frontage.pattern)
  if (!match) {
    return { 
      isValid: false, 
      message: VALIDATION_RULES.frontage.message 
    }
  }

  // Extract number and unit
  const number = parseFloat(match[1])
  const unit = match[2].toLowerCase()

  if (isNaN(number) || number <= 0) {
    return { 
      isValid: false, 
      message: 'Frontage must be a positive number' 
    }
  }

  // Normalize the value for storage
  const normalizedValue = `${number} ${unit}`
  
  return { 
    isValid: true, 
    message: '', 
    normalizedValue 
  }
}

/**
 * Validates price
 * @param {string|number} price - The price to validate
 * @returns {object} - { isValid: boolean, message: string }
 */
export function validatePrice(price) {
  if (!price || price === '') {
    return { isValid: true, message: '' } // Optional field
  }

  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  
  if (isNaN(numPrice)) {
    return { 
      isValid: false, 
      message: 'Price must be a valid number' 
    }
  }

  if (numPrice < VALIDATION_RULES.price.min || numPrice > VALIDATION_RULES.price.max) {
    return { 
      isValid: false, 
      message: VALIDATION_RULES.price.message 
    }
  }

  return { isValid: true, message: '' }
}

/**
 * Validates notes
 * @param {string} notes - The notes to validate
 * @returns {object} - { isValid: boolean, message: string }
 */
export function validateNotes(notes) {
  if (!notes || notes.trim() === '') {
    return { isValid: true, message: '' } // Optional field
  }

  if (notes.length > VALIDATION_RULES.notes.maxLength) {
    return { 
      isValid: false, 
      message: `Notes cannot exceed ${VALIDATION_RULES.notes.maxLength} characters` 
    }
  }

  return { isValid: true, message: '' }
}

/**
 * Validates stage name
 * @param {string} stage - The stage name to validate
 * @returns {object} - { isValid: boolean, message: string }
 */
export function validateStage(stage) {
  if (!stage || stage.trim() === '') {
    return { isValid: true, message: '' } // Optional field
  }

  const trimmed = stage.trim()
  
  if (trimmed.length > VALIDATION_RULES.stage.maxLength) {
    return { 
      isValid: false, 
      message: `Stage name cannot exceed ${VALIDATION_RULES.stage.maxLength} characters` 
    }
  }

  if (!VALIDATION_RULES.stage.pattern.test(trimmed)) {
    return { 
      isValid: false, 
      message: VALIDATION_RULES.stage.message 
    }
  }

  return { isValid: true, message: '' }
}

/**
 * Validates all region fields at once
 * @param {object} regionData - The region data to validate
 * @returns {object} - { isValid: boolean, errors: object }
 */
export function validateRegionData(regionData) {
  const errors = {}
  let isValid = true

  // Validate each field
  const lotNumberValidation = validateLotNumber(regionData.lotNumber)
  if (!lotNumberValidation.isValid) {
    errors.lotNumber = lotNumberValidation.message
    isValid = false
  }

  const lotSizeValidation = validateLotSize(regionData.lotSize)
  if (!lotSizeValidation.isValid) {
    errors.lotSize = lotSizeValidation.message
    isValid = false
  }

  const frontageValidation = validateFrontage(regionData.frontage)
  if (!frontageValidation.isValid) {
    errors.frontage = frontageValidation.message
    isValid = false
  }

  const priceValidation = validatePrice(regionData.price)
  if (!priceValidation.isValid) {
    errors.price = priceValidation.message
    isValid = false
  }

  const notesValidation = validateNotes(regionData.notes)
  if (!notesValidation.isValid) {
    errors.notes = notesValidation.message
    isValid = false
  }

  const stageValidation = validateStage(regionData.name)
  if (!stageValidation.isValid) {
    errors.name = stageValidation.message
    isValid = false
  }

  return { isValid, errors }
}

/**
 * Normalizes region data by applying validation and formatting
 * @param {object} regionData - The region data to normalize
 * @returns {object} - { isValid: boolean, errors: object, normalizedData: object }
 */
export function normalizeRegionData(regionData) {
  const validation = validateRegionData(regionData)
  
  if (!validation.isValid) {
    return { isValid: false, errors: validation.errors, normalizedData: null }
  }

  // Normalize the data
  const normalizedData = { ...regionData }
  
  // Apply normalized values where available
  const lotSizeValidation = validateLotSize(regionData.lotSize)
  if (lotSizeValidation.normalizedValue) {
    normalizedData.lotSize = lotSizeValidation.normalizedValue
  }

  const frontageValidation = validateFrontage(regionData.frontage)
  if (frontageValidation.normalizedValue) {
    normalizedData.frontage = frontageValidation.normalizedValue
  }

  // Trim string fields
  if (normalizedData.lotNumber) {
    normalizedData.lotNumber = normalizedData.lotNumber.trim()
  }
  if (normalizedData.name) {
    normalizedData.name = normalizedData.name.trim()
  }
  if (normalizedData.notes) {
    normalizedData.notes = normalizedData.notes.trim()
  }

  return { isValid: true, errors: {}, normalizedData }
}
